import EntityModel from './entity-model';
import PaymentInstrumentModel from './payment-instrument-model';
import ContactPhoneEmailItemsModel from './contact-phone-email-items-model';
import isNotEmpty from '@/common/helpers/is-not-empty';
import truncate from '@/common/helpers/truncate';

export default class ContactModel extends EntityModel {
    hydrate({
        id = null,
        customerId = null,
        firstName = null,
        lastName = null,
        name = null,
        organization = null,
        address = null,
        address2 = null,
        city = null,
        region = null,
        country = null,
        postalCode = null,
        phoneNumbers = null,
        emails = null,
        createdTime = null,
        updatedTime = null,
        hash = null,
        defaultPaymentInstrument = null,
        customFields = null,
    } = {}) {
        this.id = id;
        this.customerId = customerId;
        this.firstName = firstName || name;
        this.lastName = lastName;
        this.organization = organization;
        this.address = address;
        this.address2 = address2;
        this.city = city;
        this.region = region;
        this.country = country;
        this.postalCode = postalCode;
        this.phoneNumbers = (phoneNumbers || []).map(
            phoneNumber => new ContactPhoneEmailItemsModel(phoneNumber),
        );
        this.emails = (emails || []).map(
            email => new ContactPhoneEmailItemsModel(email),
        );
        this.createdTime = createdTime;
        this.updatedTime = updatedTime;
        this.hash = hash;
        this.defaultPaymentInstrument = defaultPaymentInstrument;
        this.customFields = customFields;
        this.$isContactObject = true;
    }

    get hasDefaultPaymentInstrument() {
        return this.defaultPaymentInstrument !== null;
    }

    get hasDefaultPaymentCard() {
        return this.hasDefaultPaymentInstrument
            && this.defaultPaymentInstrument.method === PaymentInstrumentModel.Methods.paymentCard;
    }

    get defaultPaymentCardId() {
        return this.defaultPaymentInstrument.paymentCardId;
    }

    get primaryEmailAddress() {
        const {value} = this.emails.find(item => !!item.primary) || {value: null};
        return value;
    }

    get primaryPhoneNumber() {
        const {value} = this.phoneNumbers.find(item => !!item.primary) || {value: null};
        return value;
    }

    get displayName() {
        const {
            fullName,
            organization,
            primaryEmailAddress,
        } = this;

        return fullName
            || `${organization || ''}`.trim()
            || primaryEmailAddress
            || truncate(this.id, {dots: true});
    }

    get fullName() {
        return `${this.firstName || ''} ${this.lastName || ''}`.trim();
    }

    get cityRegionPostalCode() {
        return [this.city, this.region, this.postalCode].filter(isNotEmpty);
    }

    get hasAddress() {
        return this.address
            || this.address2
            || this.cityRegionPostalCode.length
            || this.country;
    }

    get fullAddress() {
        return [
            this.address,
            this.address2,
            this.cityRegionPostalCode.join(', '),
            this.country,
        ].filter(item => isNotEmpty(item));
    }

    getPayload() {
        return {
            customerId: this.customerId,
            firstName: this.firstName,
            lastName: this.lastName,
            organization: this.organization,
            address: this.address,
            address2: this.address2,
            city: this.city,
            region: this.region,
            country: this.country,
            postalCode: this.postalCode,
            phoneNumbers: this.phoneNumbers.map(v => v.getPayload()),
            emails: this.emails.map(v => v.getPayload()),
            customFields: this.customFields,
        };
    }
}
